import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const HeroSkeleton = () => {
  return (
    <div className="relative w-full overflow-hidden bg-gray-200 aspect-[16/9] sm:h-[70vh]">
      <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse" />
    </div>
  );
};

const Hero = ({
  data,
  onNav,
  loading = false,
  showPromotionImage = false,
  showStatsCard = false,
  autoSlideMs = 10000,
}) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = useMemo(() => data?.slides ?? [], [data]);
  const hasMultipleSlides = slides.length > 1;

  const currentSlideData =
    slides.length > 0
      ? slides[currentSlide]
      : {
          bannerUrl: data?.bannerUrl ?? "/assets/hero.jpg",
          category: data?.category,
        };

  const banner = currentSlideData?.bannerUrl ?? "/assets/hero.jpg";

  // Helper function to generate srcset for responsive images
  const generateSrcSet = (imageUrl) => {
    if (!imageUrl) return "";
    
    // If URL is external (http/https), try to add size parameters if supported
    if (imageUrl.startsWith("http")) {
      // For external URLs, check if they support size parameters
      // Common CDN patterns: ?w=, ?width=, /w=, etc.
      const sizes = [400, 800, 1200, 1920];
      const separator = imageUrl.includes("?") ? "&" : "?";
      return sizes.map(size => `${imageUrl}${separator}w=${size} ${size}w`).join(", ");
    }
    
    // For local/relative URLs, generate srcset with common responsive breakpoints
    // Pattern: Query parameter (e.g., ?w=400)
    // Adjust this pattern based on your image service/CDN
    const sizes = [400, 800, 1200, 1920];
    
    // Pattern 1: Query parameter (e.g., /assets/hero.jpg?w=400)
    if (imageUrl.includes("?")) {
      return sizes.map(size => `${imageUrl}&w=${size} ${size}w`).join(", ");
    }
    return sizes.map(size => `${imageUrl}?w=${size} ${size}w`).join(", ");
    
    // Alternative patterns (uncomment and use if your service uses different format):
    // Pattern 2: Suffix (e.g., image-400w.jpg)
    // const baseUrl = imageUrl.replace(/\.(jpg|jpeg|png|webp)$/i, "");
    // const ext = imageUrl.match(/\.(jpg|jpeg|png|webp)$/i)?.[0] || ".jpg";
    // return sizes.map(size => `${baseUrl}-${size}w${ext} ${size}w`).join(", ");
    
    // Pattern 3: Directory structure (e.g., /images/400/image.jpg)
    // const pathParts = imageUrl.split("/");
    // const filename = pathParts.pop();
    // const basePath = pathParts.join("/");
    // return sizes.map(size => `${basePath}/${size}/${filename} ${size}w`).join(", ");
  };

  useEffect(() => {
    if (currentSlide > slides.length - 1) setCurrentSlide(0);
  }, [slides.length, currentSlide]);

  useEffect(() => {
    if (!hasMultipleSlides) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoSlideMs);

    return () => clearInterval(interval);
  }, [hasMultipleSlides, slides.length, autoSlideMs]);

  // Touch swipe handlers for mobile
  const touchStartXRef = useRef(0);
  const touchEndXRef = useRef(0);
  const isSwiping = useRef(false);

  const handleTouchStart = useCallback((e) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchEndXRef.current = e.touches[0].clientX;
    isSwiping.current = false;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndXRef.current = e.touches[0].clientX;
    const diff = Math.abs(touchStartXRef.current - touchEndXRef.current);
    if (diff > 10) {
      isSwiping.current = true;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!hasMultipleSlides) return;
    
    const diff = touchStartXRef.current - touchEndXRef.current;
    const threshold = 50; // minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left -> next slide
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      } else {
        // Swipe right -> prev slide
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      }
    }
  }, [hasMultipleSlides, slides.length]);

  if (loading) return <HeroSkeleton />;

  const category = currentSlideData?.category || data?.category;
  const misc = currentSlideData?.misc || data?.misc;

  const handleBannerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!category) {
      console.log("[Hero] No category found, cannot navigate");
      return;
    }

    console.log("[Hero] Banner clicked with category:", category, "misc:", misc);

    switch (category.toLowerCase()) {
      case "promotion":
        // ถ้ามี misc (promotion uuid) ให้ไปหน้า PromotionDetail
        if (misc) {
          navigate(`/promotion/${misc}`);
        } else {
          navigate("/promotion");
        }
        break;
      case "live-or-invest":
        navigate("/live-or-invest");
        break;
      case "news":
        navigate("/learn-more");
        break;
      default:
        console.log("[Hero] Unknown category:", category);
        // No navigation for unknown categories
        break;
    }
  };

  return (
    <section id="home" className="relative w-full overflow-x-hidden min-w-0">
      <div
        className={`
          relative w-full overflow-hidden min-w-0
          transition-all duration-700 ease-in-out
          touch-pan-y
          ${category ? "cursor-pointer" : ""}
        `}
        onClick={(e) => {
          // Don't trigger click if user was swiping
          if (isSwiping.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (category) handleBannerClick(e);
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={(e) => {
          if (category) {
            console.log("[Hero] Mouse down on banner, category:", category);
          }
        }}
        role={category ? "button" : undefined}
        tabIndex={category ? 0 : undefined}
        onKeyDown={(e) => {
          if (category && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            e.stopPropagation();
            handleBannerClick(e);
          }
        }}
        aria-label={category ? `Navigate to ${category}` : undefined}
      >
        {/* Banner image - flexible and responsive */}
        <img
          src={banner}
          srcSet={generateSrcSet(banner)}
          sizes="100vw"
          alt="Hero banner"
          loading="lazy"
          className="w-full h-auto max-w-full block transition-opacity duration-700"
        />
        
        {hasMultipleSlides ? (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
            <div className="flex items-center gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentSlide(index);
                  }}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentSlide
                      ? "w-8 h-2 bg-white"
                      : "w-2 h-2 bg-white/50 hover:bg-white/75"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Hero;
